import { dryRationReportData } from "@/lib/consumption";
import { isClasswiseRiceNorms } from "@/lib/riceNorms";
import { ensureRateCards } from "@/lib/useRateCardsSync";
import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Header from "@/components/poshan/Header";
import SchoolSelector from "@/components/poshan/SchoolSelector";
import InputForms from "@/components/poshan/InputForms";
import Sidebar from "@/components/poshan/Sidebar";
import Reports from "@/components/poshan/Reports";
import MobileGenerator from "@/components/poshan/MobileGenerator";
import { Button } from "@/components/ui/button";
import { Printer, FileDown, FileSpreadsheet, FileText, Sparkles } from "lucide-react";
import {
  QUARTERS,
  createMonth,
  defaultState,
  loadLocalState,
  saveLocalState,
  clearLocalState,
  defaultVendorInfo,
  money,
  qty,
} from "@/lib/poshanConstants";
import {
  buildMonthlySummaries,
  buildTotals,
  buildAnnexureEntries,
  buildOdiaReceiptGroups,
  buildIngredientWiseReport,
  buildWarnings,
} from "@/lib/poshanCompute";
import { printReport, downloadPdf, exportExcel, exportWord } from "@/lib/exporters";
import { activityApi } from "@/lib/activityApi";
import { aggregateDailyToMonthly, quarterDateRange } from "@/lib/dailyAggregate";
import { buildDayBreakdown } from "@/lib/mealPlan";
import { isMealDay } from "@/lib/dayKind";
import { dailyApi, vendorApi, cchApi, mdcfApi, generatorStateApi } from "@/lib/setupApi";
import { useActiveSchool } from "@/lib/activeSchool";
import { useMasterDataVersion } from "@/lib/masterDataBus";
import { toast } from "sonner";
import VideoTutorialButton from "@/components/poshan/VideoTutorialButton";

const REPORT_BUTTONS = [
  { key: "summary", label: "Financial Summary" },
  { key: "handReceipt", label: "Hand Receipt" },
  { key: "odiaReceipts", label: "Odia Hand Receipts" },
  { key: "dryRation", label: "Dry Ration Report" },
  { key: "fsaRice", label: "FSA · Dry Rice Distribution" },
  { key: "fsaCost", label: "FSA · Cooking Cost Distribution" },
  { key: "ogfr23", label: "OGFR-23 (UC)" },
  { key: "ogfr24", label: "OGFR-24" },
  { key: "annexure", label: "Annexure-1" },
  { key: "ingredient", label: "Ingredient Report" },
  { key: "all", label: "All Reports" },
];

export default function Generator() {
  const initial = useMemo(loadLocalState, []);
  const { active } = useActiveSchool();
  const [schoolInfo, setSchoolInfo] = useState(initial.schoolInfo);
  const [vendorInfo, setVendorInfo] = useState(initial.vendorInfo || defaultVendorInfo());
  const [months, setMonths] = useState(initial.months);
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);
  const [activeReport, setActiveReport] = useState("summary");
  const [ucMode, setUcMode] = useState("quarterly"); // "quarterly" | "monthly"
  const [ucMonthIndex, setUcMonthIndex] = useState(0); // 0|1|2 of derivedMonths for monthly UC
  const [isExporting, setIsExporting] = useState(false);
  const [cloudId, setCloudId] = useState(null);
  const [dailyEntries, setDailyEntries] = useState([]);
  const [allEntriesForSchool, setAllEntriesForSchool] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [cchs, setCchs] = useState([]);
  // ingredient overrides per month (keyed by month index → { ingredientName: { quantity, rate } })
  const [ingredientOverrides, setIngredientOverrides] = useState({});
  const reportRef = useRef(null);

  // Load vendor + CCH masters — refetched automatically whenever either is
  // edited anywhere in the app (Setup save / cross-tab). iter148.
  const { vendorsV, cchsV } = useMasterDataVersion();
  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([vendorApi.list(), cchApi.list()]);
        setVendors(v);
        setCchs(c);
        // iter150 — auto-populate empty vendor picks from the master list
        // so Hand Receipts show real vendor names without the user having
        // to manually pick them in the Vendors form. Only touches slots
        // that are currently blank so we never overwrite an explicit pick.
        // iter151 — prefer the vendor with the RICHEST address (post +
        // thana + pincode + block + district + village + father) so the
        // Odia receipt shows all fields, not just name+village. Fixes the
        // case where two vendors share a category but only one is fully
        // filled in Setup.
        setVendorInfo((cur) => {
          const scoreAddr = (v) => (v.post ? 1 : 0) + (v.thana ? 1 : 0) + (v.pincode ? 1 : 0) +
                                   (v.block ? 1 : 0) + (v.district ? 1 : 0) + (v.village ? 1 : 0) +
                                   (v.fatherName ? 1 : 0);
          const pick = (cat) => {
            const list = v.filter((x) => (x.category || "").toLowerCase() === cat);
            if (!list.length) return null;
            list.sort((a, b) => {
              const s = scoreAddr(b) - scoreAddr(a);
              if (s !== 0) return s;
              return String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""));
            });
            return list[0];
          };
          const nextIfBlank = (prefix, ven) => {
            if (!ven) return null;
            if (cur[`${prefix}Vendor`]) return null; // already picked — leave it
            const parts = [
              ven.postOdia || ven.post, ven.thanaOdia || ven.thana,
              ven.blockOdia || ven.block, ven.districtOdia || ven.district,
            ].filter(Boolean);
            return {
              [`${prefix}Vendor`]: ven.nameOdia || ven.name || "",
              [`${prefix}VendorOdia`]: ven.nameOdia || "",
              [`${prefix}EntityType`]: ven.entityType || "individual",
              [`${prefix}AgencyNameOdia`]: ven.agencyNameOdia || "",
              [`${prefix}Father`]: ven.fatherName || "",
              [`${prefix}FatherOdia`]: ven.fatherNameOdia || "",
              [`${prefix}Relation`]: ven.relationType === "husband" ? "husband" : "father",
              [`${prefix}Village`]: ven.village || "",
              [`${prefix}VillageOdia`]: ven.villageOdia || "",
              [`${prefix}Address`]: parts.length ? parts.join(", ") : (ven.address || ""),
              [`${prefix}AddressOdia`]: ven.addressOdia || "",
              [`${prefix}Post`]: ven.post || "",
              [`${prefix}PostOdia`]: ven.postOdia || "",
              [`${prefix}Thana`]: ven.thana || "",
              [`${prefix}ThanaOdia`]: ven.thanaOdia || "",
              [`${prefix}Block`]: ven.block || "",
              [`${prefix}BlockOdia`]: ven.blockOdia || "",
              [`${prefix}District`]: ven.district || "",
              [`${prefix}DistrictOdia`]: ven.districtOdia || "",
              [`${prefix}Pincode`]: ven.pincode || "",
              [`${prefix}PincodeOdia`]: ven.pincodeOdia || "",
            };
          };
          const patch = {
            ...nextIfBlank("grocery",   pick("grocery")),
            ...nextIfBlank("vegetable", pick("vegetables") || pick("vegetable")),
            ...nextIfBlank("egg",       pick("egg")),
            ...nextIfBlank("fuel",      pick("fuel")),
            ...nextIfBlank("ladoo",     pick("ladoo") || pick("nutritionladoo")),
          };
          if (Object.keys(patch).length === 0) return cur;
          return { ...cur, ...patch };
        });
      } catch (err) {
        console.error("Failed to load masters", err);
      }
    })();
  }, [vendorsV, cchsV]);

  // Auto-fill identity from active school profile (read from Setup → onboarding).
  // Fully overwrite identity fields whenever the active profile changes — Quarterly Reports
  // identity is "owned" by the school profile per product spec.
  useEffect(() => {
    if (!active) return;
    setSchoolInfo((cur) => ({
      ...cur,
      schoolName: active.schoolName ?? "",
      schoolNameOdia: active.schoolNameOdia ?? "",
      udiseCode: active.udiseCode ?? "",
      district: active.district ?? "",
      districtOdia: active.districtOdia ?? "",
      block: active.block ?? "",
      blockOdia: active.blockOdia ?? "",
      village: active.village ?? "",
      villageOdia: active.villageOdia ?? "",
      post: active.post ?? cur.post ?? "",
      postOdia: active.postOdia ?? "",
      thana: active.thana ?? cur.thana ?? "",
      thanaOdia: active.thanaOdia ?? "",
      pincode: active.pincode ?? cur.pincode ?? "",
      pincodeOdia: active.pincodeOdia ?? "",
      departmentName: active.departmentName || cur.departmentName,
      schemeName: active.schemeName || cur.schemeName,
      bankName: active.bankName ?? cur.bankName,
      bankAccountNo: active.bankAccountNo ?? cur.bankAccountNo,
      ifscCode: active.ifscCode ?? cur.ifscCode,
    }));
    // Also auto-fill the per-month enrollment for ALL months from profile master
    setMonths((curMonths) =>
      curMonths.map((m) => ({
        ...m,
        meals: {
          ...m.meals,
          balVatikaEnrollment: Number(active.balVatikaEnrollment ?? m.meals.balVatikaEnrollment) || 0,
          primaryEnrollment: Number(active.primaryEnrollment ?? m.meals.primaryEnrollment) || 0,
          upperPrimaryEnrollment: Number(active.upperPrimaryEnrollment ?? m.meals.upperPrimaryEnrollment) || 0,
        },
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.schoolName, active?.udiseCode, active?.district, active?.block, active?.village, active?.balVatikaEnrollment, active?.primaryEnrollment, active?.upperPrimaryEnrollment, active?.highSchoolEnrollment]);

  // Persist locally (instant cache for fast paint + offline fallback)
  useEffect(() => {
    saveLocalState({ schoolInfo, vendorInfo, months });
  }, [schoolInfo, vendorInfo, months]);

  // ---- BACKEND PERSISTENCE: keyed by (activeSchool, financialYear, quarter) ----
  // Loads any saved Generator state on school / FY / quarter switch.
  const generatorKey = useMemo(() => {
    const sid = active?.id;
    if (!sid) return null;
    const year = parseInt(String(schoolInfo.financialYear).split("-")[0], 10);
    // Backend expects quarter as an int (1..4). schoolInfo.quarter is "Q1"…"Q4".
    const qInt = parseInt(String(schoolInfo.quarter || "").replace(/^Q/i, ""), 10);
    if (!year || !qInt) return null;
    return { sid, year, quarter: qInt };
  }, [active?.id, schoolInfo.financialYear, schoolInfo.quarter]);

  // Track whether the current mount has already pulled this key from the backend so we
  // don't overwrite local edits with a stale snapshot during the same session.
  const lastLoadedKeyRef = useRef(null);
  // Bumped after the saved state loads so the daily auto-fill re-applies on top of it.
  const [remoteTick, setRemoteTick] = useState(0);
  const [cardsReady, setCardsReady] = useState(false);
  useEffect(() => { ensureRateCards().then(() => setCardsReady(true)); }, []);
  useEffect(() => {
    if (!generatorKey) return;
    const tag = `${generatorKey.sid}|${generatorKey.year}|${generatorKey.quarter}`;
    if (lastLoadedKeyRef.current === tag) return;
    lastLoadedKeyRef.current = tag;
    let cancelled = false;
    (async () => {
      try {
        const remote = await generatorStateApi.get(generatorKey.sid, generatorKey.year, generatorKey.quarter);
        if (cancelled || !remote || !remote.data) return;
        const d = remote.data;
        if (d.schoolInfo) setSchoolInfo((cur) => ({ ...cur, ...d.schoolInfo }));
        if (d.vendorInfo) setVendorInfo((cur) => ({ ...cur, ...d.vendorInfo }));
        if (Array.isArray(d.months) && d.months.length === 3) { setMonths(d.months); setRemoteTick((t) => t + 1); }
      } catch {
        // Silent fail — local state remains as-is.
      }
    })();
    return () => { cancelled = true; };
  }, [generatorKey]);

  // Debounced save to backend.
  useEffect(() => {
    if (!generatorKey) return;
    const t = setTimeout(() => {
      generatorStateApi.upsert({
        schoolId: generatorKey.sid,
        year: generatorKey.year,
        quarter: generatorKey.quarter,
        data: { schoolInfo, vendorInfo, months },
      }).catch((err) => console.error("Generator state save failed", err));
    }, 800); // debounce noisy edits
    return () => clearTimeout(t);
  }, [generatorKey, schoolInfo, vendorInfo, months]);

  // Fetch daily entries when school + quarter + FY changes (use active profile id when present)
  useEffect(() => {
    const sid = active?.id || cloudId;
    if (!sid) {
      setDailyEntries([]);
      setAllEntriesForSchool([]);
      return;
    }
    const range = quarterDateRange(schoolInfo.financialYear, schoolInfo.quarter);
    if (!range) {
      setDailyEntries([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Fetch quarter-scoped entries (drives auto-fill) AND all-school entries (drives the
        // "switch to a different quarter" hint when user has data outside current selection).
        const [data, allData] = await Promise.all([
          dailyApi.list({ schoolId: sid, fromDate: range.fromDate, toDate: range.toDate }),
          dailyApi.list({ schoolId: sid }),
        ]);
        if (!cancelled) {
          setDailyEntries(data);
          setAllEntriesForSchool(allData);
        }
      } catch (err) {
        console.error("Failed to load daily entries", err);
        if (!cancelled) {
          setDailyEntries([]);
          setAllEntriesForSchool([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [active, cloudId, schoolInfo.financialYear, schoolInfo.quarter]);

  // Detect daily entries that fall OUTSIDE the currently selected FY+Quarter so we can
  // surface a one-click "Switch to {detected}" hint to the user.
  const outsideHint = useMemo(() => {
    if (!allEntriesForSchool?.length) return null;
    const inRange = new Set(dailyEntries.map((e) => e.id));
    const outside = allEntriesForSchool.filter((e) => !inRange.has(e.id));
    if (outside.length === 0) return null;
    // Pick the most recent outside entry → derive its FY + Quarter.
    const latest = outside.reduce((a, b) => ((a.date || "") > (b.date || "") ? a : b));
    const [yStr, mStr] = latest.date.split("-");
    const monthIdx = Number(mStr) - 1; // 0-based
    const year = Number(yStr);
    const fyStart = monthIdx >= 3 ? year : year - 1;
    const fy = `${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}`;
    const q =
      monthIdx >= 3 && monthIdx <= 5 ? "Q1" :
      monthIdx >= 6 && monthIdx <= 8 ? "Q2" :
      monthIdx >= 9 && monthIdx <= 11 ? "Q3" : "Q4";
    if (fy === schoolInfo.financialYear && q === schoolInfo.quarter) return null;
    return { count: outside.length, fy, q, latestDate: latest.date };
  }, [allEntriesForSchool, dailyEntries, schoolInfo.financialYear, schoolInfo.quarter]);

  const quarterLabel = QUARTERS[schoolInfo.quarter].label;

  // Daily-aggregated months — replaces ingredients and provides classwise breakdown
  const derivedMonths = useMemo(() => {
    const base = aggregateDailyToMonthly(months, schoolInfo.financialYear, dailyEntries);
    // Apply manual overrides on top
    return base.map((m, idx) => {
      const ovr = ingredientOverrides[idx];
      if (!ovr) return m;
      return {
        ...m,
        ingredients: m.ingredients.map((ing) => {
          const o = ovr[ing.name];
          if (!o) return ing;
          // Override: drop the preserved daily-summed amount so the recomputed
          // qty × rate is used for Annexure-1 / monthly totals.
          return { ...ing, quantity: o.quantity ?? ing.quantity, rate: o.rate ?? ing.rate, amount: undefined, isOverride: true };
        }),
      };
    });
  }, [months, schoolInfo.financialYear, dailyEntries, ingredientOverrides, cardsReady]);
  const dailyAggregateActive = derivedMonths.some((m) => m._ingredientsFromDaily);

  // Auto-fill funds.{class}.expenditure + grains.{class}Rice.consumption from daily entries.
  // Runs whenever derivedMonths picks up new daily-derived values.
  useEffect(() => {
    setMonths((cur) => {
      let changed = false;
      const next = cur.map((m, idx) => {
        const d = derivedMonths[idx];
        if (!d || (!d._fundsByClassFromDaily && !d._riceByClassFromDaily)) return m;
        const newFunds = { ...m.funds };
        const newGrains = { ...m.grains };
        let touched = false;
        if (d._fundsByClassFromDaily) {
          ["balVatika", "primary", "upperPrimary"].forEach((k) => {
            const v = Number((d._fundsByClassFromDaily[k] || 0).toFixed(2));
            if (Number(newFunds[k].expenditure || 0) !== v) {
              newFunds[k] = { ...newFunds[k], expenditure: v };
              touched = true;
            }
          });
        }
        if (d._riceByClassFromDaily) {
          [
            ["balVatika", "balVatikaRice"],
            ["primary", "primaryRice"],
            ["upperPrimary", "upperPrimaryRice"],
          ].forEach(([cls, key]) => {
            const v = Number((d._riceByClassFromDaily[cls] || 0).toFixed(3));
            if (Number(newGrains[key].consumption || 0) !== v) {
              newGrains[key] = { ...newGrains[key], consumption: v };
              touched = true;
            }
          });
        }
        if (touched) {
          changed = true;
          return { ...m, funds: newFunds, grains: newGrains };
        }
        return m;
      });
      return changed ? next : cur;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(derivedMonths.map((m) => ({ f: m._fundsByClassFromDaily, r: m._riceByClassFromDaily }))), remoteTick]);

  // Dry-Ration aggregate for the selected quarter — drives the Dry Ration Report tab.
  // Shared helper; legacy group rice rule until the official norms are confirmed (switch OFF).
  const dryRationData = useMemo(() => dryRationReportData(dailyEntries, { drGroupRice: !isClasswiseRiceNorms() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dailyEntries, cardsReady]);

  // Edit ingredient handler (override for daily-derived ingredients)
  const editIngredient = (monthIdx, name, fields) => {
    setIngredientOverrides((cur) => ({
      ...cur,
      [monthIdx]: { ...(cur[monthIdx] || {}), [name]: { ...(cur[monthIdx]?.[name] || {}), ...fields } },
    }));
  };
  const clearOverride = (monthIdx, name) => {
    setIngredientOverrides((cur) => {
      const month = { ...(cur[monthIdx] || {}) };
      delete month[name];
      return { ...cur, [monthIdx]: month };
    });
  };

  // Computations (use derivedMonths so reports reflect daily aggregation)
  const monthlySummaries = useMemo(() => buildMonthlySummaries(derivedMonths), [derivedMonths]);
  const totals = useMemo(() => buildTotals(derivedMonths, monthlySummaries), [derivedMonths, monthlySummaries]);
  const annexureEntries = useMemo(
    () => buildAnnexureEntries(derivedMonths, schoolInfo, vendorInfo, cchs, dryRationData),
    [derivedMonths, schoolInfo, vendorInfo, cchs, dryRationData],
  );
  const odiaReceiptGroups = useMemo(
    () => buildOdiaReceiptGroups(derivedMonths, schoolInfo, vendorInfo, cchs, vendors),
    [derivedMonths, schoolInfo, vendorInfo, cchs, vendors],
  );
  const ingredientWiseReport = useMemo(() => buildIngredientWiseReport(derivedMonths), [derivedMonths]);
  const warnings = useMemo(() => buildWarnings(schoolInfo, derivedMonths, totals), [schoolInfo, derivedMonths, totals]);

  // ---------- Monthly / Quarterly UC scope ----------
  const ucScopedMonths = useMemo(() => {
    if (ucMode === "monthly") {
      const safeIdx = Math.min(Math.max(ucMonthIndex, 0), derivedMonths.length - 1);
      return derivedMonths.slice(safeIdx, safeIdx + 1);
    }
    return derivedMonths;
  }, [ucMode, ucMonthIndex, derivedMonths]);

  const ucMonthlySummaries = useMemo(() => buildMonthlySummaries(ucScopedMonths), [ucScopedMonths]);
  const ucTotals = useMemo(() => buildTotals(ucScopedMonths, ucMonthlySummaries), [ucScopedMonths, ucMonthlySummaries]);
  const ucAnnexureEntries = useMemo(
    () => buildAnnexureEntries(ucScopedMonths, schoolInfo, vendorInfo, cchs, dryRationData),
    [ucScopedMonths, schoolInfo, vendorInfo, cchs, dryRationData],
  );
  const ucScope = useMemo(() => {
    if (ucMode === "monthly") {
      const m = ucScopedMonths[0];
      return {
        mode: "monthly",
        label: m ? `${m.monthName} ${schoolInfo.financialYear}` : "Monthly",
        periodNote: "Monthly UC",
      };
    }
    return {
      mode: "quarterly",
      label: `${QUARTERS[schoolInfo.quarter].label} ${schoolInfo.financialYear}`,
      periodNote: "Quarterly UC",
    };
  }, [ucMode, ucScopedMonths, schoolInfo.financialYear, schoolInfo.quarter]);

  // Updaters
  const updateSchoolInfo = (key, value) => setSchoolInfo((cur) => ({ ...cur, [key]: value }));
  const updateVendorInfo = (key, value) => setVendorInfo((cur) => ({ ...cur, [key]: value }));

  // Apply vendor master pick to inline vendor fields
  const applyVendorPick = (prefix, vendor) => {
    setVendorInfo((cur) => ({
      ...cur,
      [`${prefix}Vendor`]: vendor.name || "",
      [`${prefix}VendorOdia`]: vendor.nameOdia || "",
      // iter124 — propagate entity type + agency-name so hand-receipts can
      // render the agency variant (drops father line, shows agency name).
      [`${prefix}EntityType`]: vendor.entityType || "individual",
      [`${prefix}AgencyNameOdia`]: vendor.agencyNameOdia || "",
      [`${prefix}Father`]: vendor.fatherName || "",
      [`${prefix}FatherOdia`]: vendor.fatherNameOdia || "",
      [`${prefix}Relation`]: vendor.relationType === "husband" ? "husband" : "father",
      [`${prefix}Village`]: vendor.village || "",
      [`${prefix}VillageOdia`]: vendor.villageOdia || "",
      [`${prefix}Address`]: vendor.address || "",
      [`${prefix}AddressOdia`]: vendor.addressOdia || "",
      [`${prefix}Post`]: vendor.post || "",
      [`${prefix}PostOdia`]: vendor.postOdia || "",
      [`${prefix}Thana`]: vendor.thana || "",
      [`${prefix}ThanaOdia`]: vendor.thanaOdia || "",
      [`${prefix}Block`]: vendor.block || "",
      [`${prefix}BlockOdia`]: vendor.blockOdia || "",
      [`${prefix}District`]: vendor.district || "",
      [`${prefix}DistrictOdia`]: vendor.districtOdia || "",
      [`${prefix}Pincode`]: vendor.pincode || "",
      [`${prefix}PincodeOdia`]: vendor.pincodeOdia || "",
    }));
    toast.success(`${vendor.name} loaded`);
  };

  // Apply CCH master pick to a cook row
  const applyCookPick = (monthIdx, cookIdx, cch) => {
    setMonths((cur) =>
      cur.map((m, i) => {
        if (i !== monthIdx) return m;
        return {
          ...m,
          cooks: m.cooks.map((c, k) => (k === cookIdx ? {
            ...c,
            name: cch.name || "",
            nameOdia: cch.nameOdia || "",
            mode: cch.defaultMode || c.mode,
            cchId: cch.id,
            // iter124
            entityType: cch.entityType || "individual",
            agencyNameOdia: cch.agencyNameOdia || "",
            father: cch.fatherName || "",
            fatherOdia: cch.fatherNameOdia || "",
            village: cch.village || "",
            villageOdia: cch.villageOdia || "",
            address: cch.address || "",
            addressOdia: cch.addressOdia || "",
            post: cch.post || "",
            postOdia: cch.postOdia || "",
            thana: cch.thana || "",
            thanaOdia: cch.thanaOdia || "",
            block: cch.block || "",
            blockOdia: cch.blockOdia || "",
            district: cch.district || "",
            districtOdia: cch.districtOdia || "",
            pincode: cch.pincode || "",
            pincodeOdia: cch.pincodeOdia || "",
          } : c)),
        };
      }),
    );
    toast.success(`${cch.name} loaded`);
  };

  const handleQuarterChange = (quarter) => {
    setSchoolInfo((cur) => ({ ...cur, quarter }));
    setMonths((cur) =>
      QUARTERS[quarter].months.map((monthName, i) => ({ ...(cur[i] ?? createMonth(monthName)), monthName })),
    );
    setActiveMonthIndex(0);
  };

  const updateMeal = (idx, key, value) =>
    setMonths((cur) => cur.map((m, i) => (i === idx ? { ...m, meals: { ...m.meals, [key]: value } } : m)));

  const updateFund = (idx, key, field, value) =>
    setMonths((cur) =>
      cur.map((m, i) =>
        i === idx ? { ...m, funds: { ...m.funds, [key]: { ...m.funds[key], [field]: value } } } : m,
      ),
    );

  const updateGrain = (idx, key, field, value) =>
    setMonths((cur) =>
      cur.map((m, i) =>
        i === idx ? { ...m, grains: { ...m.grains, [key]: { ...m.grains[key], [field]: value } } } : m,
      ),
    );

  const updateIngredient = (idx, ingIdx, field, value) =>
    setMonths((cur) =>
      cur.map((m, i) => {
        if (i !== idx) return m;
        return {
          ...m,
          ingredients: m.ingredients.map((ing, k) => (k === ingIdx ? { ...ing, [field]: value } : ing)),
        };
      }),
    );

  const addIngredient = (idx) =>
    setMonths((cur) =>
      cur.map((m, i) =>
        i === idx
          ? {
              ...m,
              ingredients: [
                ...m.ingredients,
                { id: `custom-${Date.now()}`, name: "Custom Ingredient", quantity: 0, unit: "kg", rate: 0, isCustom: true },
              ],
            }
          : m,
      ),
    );

  const removeIngredient = (idx, ingIdx) =>
    setMonths((cur) =>
      cur.map((m, i) =>
        i === idx ? { ...m, ingredients: m.ingredients.filter((_, k) => k !== ingIdx) } : m,
      ),
    );

  const updateCook = (idx, cookIdx, field, value) =>
    setMonths((cur) =>
      cur.map((m, i) => {
        if (i !== idx) return m;
        return { ...m, cooks: m.cooks.map((c, k) => (k === cookIdx ? { ...c, [field]: value } : c)) };
      }),
    );

  const addCook = (idx) =>
    setMonths((cur) =>
      cur.map((m, i) =>
        i === idx
          ? { ...m, cooks: [...m.cooks, { id: `cook-${Date.now()}`, name: "", amount: 0, mode: "Bank" }] }
          : m,
      ),
    );

  const removeCook = (idx, cookIdx) =>
    setMonths((cur) => cur.map((m, i) => (i === idx ? { ...m, cooks: m.cooks.filter((_, k) => k !== cookIdx) } : m)));

  // Cloud handlers
  const handleLoadCloud = ({ id, schoolInfo: si, vendorInfo: vi, months: mo }) => {
    setSchoolInfo({ ...defaultState().schoolInfo, ...si });
    setVendorInfo({ ...defaultVendorInfo(), ...(vi || {}) });
    if (Array.isArray(mo) && mo.length === 3) {
      setMonths(mo);
    }
    setCloudId(id);
    setActiveMonthIndex(0);
  };

  const handleResetDraft = () => {
    const fresh = defaultState();
    setSchoolInfo(fresh.schoolInfo);
    setVendorInfo(fresh.vendorInfo);
    setMonths(fresh.months);
    setActiveMonthIndex(0);
    setActiveReport("summary");
    setCloudId(null);
    clearLocalState();
    toast.success("Started a fresh draft");
  };

  // Exports
  const onPrint = () => printReport();

  // One-click bootstrap of the current quarter's 3 months from saved monthly MDCFs.
  // Pulls expenditure (Cooking Cost) and rice consumption from each month's MDCF.
  const bootstrapFromMdcfs = async () => {
    const sid = active?.id || cloudId;
    if (!sid) {
      toast.error("Pick an active school first");
      return;
    }
    const monthDefs = QUARTERS[schoolInfo.quarter]?.months || [];
    const fyStartYear = parseInt(String(schoolInfo.financialYear).split("-")[0], 10);
    if (!fyStartYear) {
      toast.error("Set the Financial Year first");
      return;
    }
    setIsExporting(true);
    try {
      const fetched = await Promise.all(
        monthDefs.map((md) => {
          // FY runs Apr–Mar: months Apr–Dec belong to fyStartYear, Jan–Mar to fyStartYear+1.
          const m = md.value; // 1..12
          const yr = m >= 4 ? fyStartYear : fyStartYear + 1;
          return mdcfApi.get(sid, yr, m).catch(() => null);
        }),
      );
      let appliedCount = 0;
      const fmtToNum = (v) => {
        if (v === undefined || v === null || v === "") return 0;
        if (typeof v === "string" && v.includes("=")) {
          // Use the part AFTER the "=" sign
          const total = v.split("=").pop();
          return Number(total) || 0;
        }
        return Number(v) || 0;
      };
      setMonths((cur) => cur.map((mn, idx) => {
        const rec = fetched[idx];
        if (!rec || !rec.data) return mn;
        const md = rec.data;
        const newFunds = { ...mn.funds };
        const newGrains = { ...mn.grains };
        // Section 6 expenses → funds.{class}.expenditure
        const sec6 = md?.monthly?.section6 || {};
        ["balVatika", "primary", "upperPrimary"].forEach((kFE, i) => {
          const sourceKey = ["balBatika", "primaryClass", "upperPrimaryClass"][i];
          const v = fmtToNum(sec6[sourceKey]?.expense);
          if (v > 0) newFunds[kFE] = { ...newFunds[kFE], expenditure: Number(v.toFixed(2)) };
        });
        // Section 7 rice → grains.{class}Rice.consumption (kg)
        const sec7 = md?.monthly?.section7 || {};
        const grainMap = [["balVatikaRice", "balBatika"], ["primaryRice", "primaryClass"], ["upperPrimaryRice", "upperPrimaryClass"]];
        grainMap.forEach(([gKey, sKey]) => {
          const v = fmtToNum(sec7[sKey]?.expense);
          if (v > 0) newGrains[gKey] = { ...newGrains[gKey], consumption: Number(v.toFixed(3)) };
        });
        appliedCount += 1;
        return { ...mn, funds: newFunds, grains: newGrains };
      }));
      if (appliedCount === 0) {
        toast.warning(`No MDCFs found for ${quarterLabel}. Save MDCFs in /mdcf first, then try again.`);
      } else {
        toast.success(`Bootstrapped from ${appliedCount}/${monthDefs.length} MDCFs`);
      }
    } catch (err) {
      console.error("MDCF bootstrap failed", err);
      toast.error("Bootstrap failed");
    } finally {
      setIsExporting(false);
    }
  };

  const onPdf = async () => {
    setIsExporting(true);
    try {
      await downloadPdf(reportRef, schoolInfo, activeReport);
      toast.success("PDF downloaded");
      // Live-feed: counts as a Voucher when the active report is one of the
      // hand-receipt / annexure tabs, else as a UC.
      const isVoucherTab = activeReport === "handReceipt" || activeReport === "annexure";
      activityApi.log({
        type: isVoucherTab ? "voucher_generated" : "uc_generated",
        extra: `${quarterLabel} · ${activeReport}`,
      }).catch(() => {});
    } catch (err) {
      console.error(err);
      toast.error("Could not generate PDF");
    } finally {
      setIsExporting(false);
    }
  };
  const onExcel = () => {
    exportExcel({ schoolInfo, quarterLabel, totals, ingredientWiseReport, annexureEntries });
    toast.success("Excel file downloaded");
  };
  const onWord = () => {
    exportWord({ schoolInfo, quarterLabel, totals, ingredientWiseReport, annexureEntries });
    toast.success("Word file downloaded");
  };

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />

      {/* Mobile-only layout (<md). Reuses parent state via props. */}
      <MobileGenerator
        schoolInfo={schoolInfo}
        vendorInfo={vendorInfo}
        months={derivedMonths}
        rawMonths={months}
        activeMonthIndex={activeMonthIndex}
        setActiveMonthIndex={setActiveMonthIndex}
        handleQuarterChange={handleQuarterChange}
        updateMeal={updateMeal}
        updateFund={updateFund}
        totals={totals}
        monthlySummaries={monthlySummaries}
        ingredientWiseReport={ingredientWiseReport}
        warnings={warnings}
        outsideHint={outsideHint}
        onPdf={onPdf}
        onExcel={onExcel}
        onWord={onWord}
        onPrint={onPrint}
        isExporting={isExporting}
        setActiveReport={setActiveReport}
        activeReport={activeReport}
        reportButtons={REPORT_BUTTONS}
        bootstrapFromMdcfs={bootstrapFromMdcfs}
        activeSchool={active}
      />

      <div className="hidden md:block">
      <Header />

      {/* In-portal Odia tutorial CTA — auditors / new schools can re-watch
          the full MDM Bill · UC · Hand Receipts walkthrough any time. The
          bare YouTube URL is NOT rendered to DOM; it only appears inside
          the iframe `src` once the modal opens. */}
      <div className="mx-auto max-w-7xl px-4 pt-5 lg:px-8 no-print" data-testid="generator-tutorial-cta">
        <VideoTutorialButton
          youtubeId="T3Ym27t4Rhc"
          titleOdia="📊 MDM Bill · UC · Hand Receipts କିପରି ତିଆରି ହେବ"
          subtitleOdia="Portal ରେ ସମ୍ପୂର୍ଣ୍ଣ ପ୍ରକ୍ରିୟା — Live Demo · ଏକ ଭିଡିଓ ଦେଖିଲେ ୧୦୦% ସ୍ପଷ୍ଟ"
          dialogTitleOdia="MDM Bill, UC & Hand Receipts on the Portal"
          durationLabel="▶ Full Walkthrough"
          variant="moss"
          testId="generator-video-tutorial"
        />
      </div>

      <SchoolSelector
        state={{ schoolInfo, vendorInfo, months }}
        cloudId={cloudId}
        onLoadCloud={handleLoadCloud}
        onSavedToCloud={(id) => setCloudId(id)}
        onResetDraft={handleResetDraft}
        onClearCloudId={() => setCloudId(null)}
      />

      {/* Quick Stats Strip */}
      <div className="border-b border-border bg-white no-print">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-5 text-sm sm:grid-cols-4 lg:px-8">
          {[
            ["Quarter", quarterLabel],
            ["Meals Served", qty(totals.totalMealsServed)],
            ["Grant Received", money(totals.totalGrantReceived)],
            ["UC Utilized", money(totals.ucUtilizedAmount)],
          ].map(([label, value]) => (
            <div key={label} data-testid={`stat-${label}`} className="border-l-2 border-clay/40 pl-3">
              <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-700">{label}</span>
              <b className="text-base text-ink-900">{value}</b>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {outsideHint && (
          <div
            data-testid="outside-quarter-hint"
            className="no-print mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-clay bg-clay-50 px-4 py-3 text-sm text-clay-dark"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              You have <b>{outsideHint.count}</b> daily {outsideHint.count === 1 ? "entry" : "entries"} saved for this school in <b>{QUARTERS[outsideHint.q].label} · FY {outsideHint.fy}</b> (latest: {outsideHint.latestDate}). They are not showing here because the current selection is <b>FY {schoolInfo.financialYear} · {QUARTERS[schoolInfo.quarter].label}</b>.
            </span>
            <Button
              size="sm"
              data-testid="switch-to-detected-fy-btn"
              onClick={() => {
                setSchoolInfo((cur) => ({ ...cur, financialYear: outsideHint.fy }));
                handleQuarterChange(outsideHint.q);
                toast.success(`Switched to ${QUARTERS[outsideHint.q].label} · FY ${outsideHint.fy}`);
              }}
              className="bg-clay text-white hover:bg-clay-dark"
            >
              Switch to {outsideHint.q} · FY {outsideHint.fy}
            </Button>
          </div>
        )}

        <div className="no-print grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <InputForms
            schoolInfo={schoolInfo}
            vendorInfo={vendorInfo}
            months={derivedMonths}
            rawMonths={months}
            dailyAggregateActive={dailyAggregateActive}
            cloudId={cloudId}
            activeSchool={active}
            vendors={vendors}
            cchs={cchs}
            activeMonthIndex={activeMonthIndex}
            setActiveMonthIndex={setActiveMonthIndex}
            updateSchoolInfo={updateSchoolInfo}
            updateVendorInfo={updateVendorInfo}
            applyVendorPick={applyVendorPick}
            handleQuarterChange={handleQuarterChange}
            updateMeal={updateMeal}
            updateFund={updateFund}
            updateGrain={updateGrain}
            updateIngredient={updateIngredient}
            addIngredient={addIngredient}
            removeIngredient={removeIngredient}
            updateCook={updateCook}
            applyCookPick={applyCookPick}
            addCook={addCook}
            removeCook={removeCook}
            editIngredient={editIngredient}
            clearOverride={clearOverride}
            monthlySummaries={monthlySummaries}
          />

          <Sidebar
            totals={totals}
            warnings={warnings}
            schoolInfo={schoolInfo}
            updateSchoolInfo={updateSchoolInfo}
          />
        </div>

        {/* Report Preview Controls */}
        <section
          data-testid="report-controls"
          className="mt-10 rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8 no-print"
        >
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">Reports</p>
              <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
                Preview &amp; Export
              </h2>
            </div>
            <p className="max-w-xl text-sm leading-relaxed text-ink-700">
              Pick a report below — the preview appears underneath. Print, or download as PDF, Excel or Word.
            </p>
          </div>

          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Button onClick={onPrint} data-testid="btn-print" className="gap-2 bg-ink-900 text-white hover:bg-ink-700">
              <Printer className="h-4 w-4" /> Print
            </Button>
            <Button onClick={onPdf} disabled={isExporting} data-testid="btn-pdf" className="gap-2 bg-clay text-white hover:bg-clay-dark">
              <FileDown className="h-4 w-4" /> {isExporting ? "Preparing PDF…" : "Download PDF"}
            </Button>
            <Button onClick={onExcel} data-testid="btn-excel" className="gap-2 bg-moss text-white hover:bg-moss-dark">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </Button>
            <Button onClick={onWord} data-testid="btn-word" variant="outline" className="gap-2">
              <FileText className="h-4 w-4" /> Word
            </Button>
            <Button onClick={() => setActiveReport("all")} data-testid="btn-all" variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4" /> Generate All
            </Button>
            <Button onClick={bootstrapFromMdcfs} data-testid="btn-bootstrap-mdcfs" variant="outline" className="gap-2 border-moss text-moss-dark hover:bg-moss/10">
              <Sparkles className="h-4 w-4" /> Bootstrap from MDCFs
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {REPORT_BUTTONS.map((r) => (
              <button
                key={r.key}
                data-testid={`tab-report-${r.key}`}
                onClick={() => setActiveReport(r.key)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${
                  activeReport === r.key
                    ? "border-clay bg-clay text-white"
                    : "border-border bg-white text-ink-900 hover:border-clay hover:text-clay"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* UC Mode toggle (Monthly / Quarterly) — affects OGFR-23 + OGFR-24+Annexure */}
          {(activeReport === "ogfr23" || activeReport === "ogfr24" || activeReport === "annexure" || activeReport === "all") && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-clay/30 bg-clay/5 p-3" data-testid="uc-mode-row">
              <span className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-clay">UC Period</span>
              <div className="inline-flex rounded-full border border-border bg-white p-1">
                {[
                  { v: "monthly", label: "Monthly UC" },
                  { v: "quarterly", label: "Quarterly UC" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`uc-mode-${opt.v}`}
                    onClick={() => setUcMode(opt.v)}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-150 ${
                      ucMode === opt.v ? "bg-clay text-white shadow-sm" : "text-ink-700 hover:text-clay"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {ucMode === "monthly" && (
                <label className="flex items-center gap-2 text-xs font-semibold text-ink-700" data-testid="uc-month-picker">
                  Month:
                  <select
                    value={ucMonthIndex}
                    onChange={(e) => setUcMonthIndex(Number(e.target.value))}
                    data-testid="uc-month-select"
                    className="h-9 rounded-md border border-border bg-white px-3 text-sm font-normal focus:border-clay focus:outline-none"
                  >
                    {derivedMonths.map((m, i) => (
                      <option key={m.monthName + i} value={i}>{m.monthName}</option>
                    ))}
                  </select>
                </label>
              )}
              <span className="ml-auto rounded-full bg-clay/15 px-3 py-1 text-xs font-semibold text-clay" data-testid="uc-scope-label">
                Showing: <b>{ucScope.label}</b>
              </span>
            </div>
          )}
        </section>
      </div>
      </div>
      {/* End desktop-only wrapper. Reports below render on mobile too so the
          "Generate Report" sticky CTA can scroll into view. */}

      <div className="mx-auto mt-6 max-w-7xl px-4 sm:px-6 md:mt-8 lg:px-8">
        <div id="report-output" ref={reportRef} data-testid="report-output">
          <Reports
            activeReport={activeReport}
            schoolInfo={schoolInfo}
            totals={(activeReport === "ogfr23" || activeReport === "ogfr24" || activeReport === "all") ? ucTotals : totals}
            monthlySummaries={monthlySummaries}
            months={months}
            odiaReceiptGroups={odiaReceiptGroups}
            annexureEntries={(activeReport === "ogfr24" || activeReport === "annexure" || activeReport === "all") ? ucAnnexureEntries : annexureEntries}
            ingredientWiseReport={ingredientWiseReport}
            dryRationData={dryRationData}
            ucScope={ucScope}
          />
        </div>
      </div>

      <footer className="border-t border-border bg-sand-100 py-8 no-print">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-ink-700">
            Designed for Odisha Government Schools · Built for teachers — quarterly PM POSHAN reports without the spreadsheet headache.
          </p>
        </div>
      </footer>
    </main>
  );
}
